import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, Pencil, Trash2, FileText, Paperclip, Download, Search, Wallet, ChevronLeft, ChevronRight, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button, Badge, Field, Input, Select, Textarea, Modal } from "@/components/ui-kit";
import { DatePicker } from "@/components/DatePicker";

/**
 * Iter v32 — Hutang Karyawan admin page (/hutang-karyawan).
 * super_admin / finance only. CRUD with file attachment for proof.
 * Log paginated 20/page, newest first.
 */

const fmtIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDT = (ts) => { try { return new Date(ts).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ts || ""; } };
const fmtD = (d) => { try { return new Date(`${d}T00:00:00`).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d || ""; } };

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result || "").toString().split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function HutangKaryawan() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filterEmpId, setFilterEmpId] = useState("");
  const [data, setData] = useState({ rows: [], total: 0, page: 1, per_page: 20, total_pages: 1, balances: {} });
  const [balances, setBalances] = useState([]);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ employee_id: "", type: "debt", amount: "", note: "", entry_date: "", attachment_b64: "", attachment_filename: "", attachment_mime: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/employee-debts", { params: { employee_id: filterEmpId || undefined, page, per_page: 20 } });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  };
  const loadRefs = async () => {
    const [e, b] = await Promise.all([api.get("/employees"), api.get("/employee-debts/balances").catch(() => ({ data: [] }))]);
    setEmployees(e.data);
    setBalances(b.data || []);
  };
  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterEmpId, page]);

  const openNew = (type = "debt") => {
    setEditing(null);
    setForm({ employee_id: filterEmpId || "", type, amount: "", note: "", entry_date: new Date().toISOString().slice(0, 10), attachment_b64: "", attachment_filename: "", attachment_mime: "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ employee_id: r.employee_id, type: r.type, amount: String(r.amount), note: r.note || "", entry_date: r.entry_date || String(r.ts || "").slice(0, 10), attachment_b64: "", attachment_filename: "", attachment_mime: "" });
    setOpen(true);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3_000_000) return toast.error("File maksimal 3MB");
    const b64 = await fileToBase64(f);
    setForm((s) => ({ ...s, attachment_b64: b64, attachment_filename: f.name, attachment_mime: f.type }));
  };

  const save = async () => {
    if (!form.employee_id) return toast.error("Pilih karyawan");
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Jumlah harus > 0");
    const payload = { ...form, amount: amt };
    try {
      if (editing) await api.put(`/employee-debts/${editing.id}`, payload);
      else await api.post("/employee-debts", payload);
      toast.success(editing ? "Diperbarui" : "Disimpan");
      setOpen(false);
      load(); loadRefs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus catatan ${r.type === "debt" ? "hutang" : "pembayaran"} Rp ${fmtIDR(r.amount)} untuk ${r.employee_name}?`)) return;
    try { await api.delete(`/employee-debts/${r.id}`); toast.success("Dihapus"); load(); loadRefs(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const totalDebt = useMemo(() => balances.reduce((s, b) => s + (b.balance > 0 ? b.balance : 0), 0), [balances]);
  const totalCredit = useMemo(() => balances.reduce((s, b) => s + (b.balance < 0 ? -b.balance : 0), 0), [balances]);

  return (
    <div className="space-y-6" data-testid="hutang-karyawan-page">
      <PageHeader overline="Admin · Finance" title="Hutang Karyawan"
        description="Catat hutang & pembayaran karyawan, lampirkan bukti file, lihat log lengkap." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-widest text-stone-500">Karyawan Berhutang</div>
          <div className="text-2xl font-display font-bold text-red-700 mt-1" data-testid="hutang-stat-debtors">{balances.filter((b) => b.balance > 0).length}</div>
        </Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Outstanding Hutang</div>
          <div className="text-xl font-display font-bold text-red-700 mt-1 font-mono" data-testid="hutang-stat-total">{fmtIDR(totalDebt)}</div>
        </Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-widest text-stone-500">Saldo Lebih (Credit)</div>
          <div className="text-xl font-display font-bold text-emerald-700 mt-1 font-mono">{fmtIDR(totalCredit)}</div>
        </Card>
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3">
        <Field label="Filter karyawan" className="flex-1 min-w-[200px]">
          <Select value={filterEmpId} onChange={(e) => { setPage(1); setFilterEmpId(e.target.value); }} data-testid="hutang-filter-emp">
            <option value="">— Semua karyawan —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name} {e.employee_code && `(${e.employee_code})`}</option>)}
          </Select>
        </Field>
        <Button onClick={() => openNew("debt")} className="!bg-red-600 hover:!bg-red-700" data-testid="hutang-add-debt">
          <ArrowUpCircle className="h-3.5 w-3.5" /> Tambah Hutang
        </Button>
        <Button onClick={() => openNew("payment")} className="!bg-emerald-600 hover:!bg-emerald-700" data-testid="hutang-add-payment">
          <ArrowDownCircle className="h-3.5 w-3.5" /> Catat Pembayaran
        </Button>
      </Card>

      {/* Per-employee balances overview */}
      {balances.length > 0 && (
        <Card className="p-4">
          <h3 className="text-xs uppercase tracking-widest text-stone-500 mb-3">Saldo Per Karyawan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {balances.map((b) => (
              <button key={b.id} onClick={() => { setPage(1); setFilterEmpId(b.id); }}
                className="text-left p-2 border border-stone-200 hover:bg-stone-50 flex items-center justify-between"
                data-testid={`hutang-balance-${b.id}`}>
                <div>
                  <div className="font-medium text-sm text-stone-900">{b.name}</div>
                  <div className="text-[10px] text-stone-500">{b.department_category || "—"}</div>
                </div>
                <div className={`font-mono text-sm font-bold ${b.balance > 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtIDR(b.balance)}</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Log table */}
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-3">Waktu</th>
              <th className="text-left px-3 py-3">Karyawan</th>
              <th className="text-center px-3 py-3">Tipe</th>
              <th className="text-right px-3 py-3">Jumlah</th>
              <th className="text-left px-3 py-3">Catatan</th>
              <th className="text-center px-3 py-3">Bukti</th>
              <th className="text-right px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100" data-testid="hutang-log-tbody">
            {loading && <tr><td colSpan={7} className="px-3 py-12 text-center text-xs text-stone-400">Memuat…</td></tr>}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada catatan hutang.</td></tr>
            )}
            {!loading && data.rows.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`hutang-row-${r.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs">
                  {r.entry_date ? fmtD(r.entry_date) : fmtDT(r.ts)}
                  {r.entry_date && <div className="text-[10px] text-stone-400">dicatat {fmtDT(r.ts)}</div>}
                </td>
                <td className="px-3 py-2.5"><div className="font-medium text-stone-900">{r.employee_name}</div></td>
                <td className="px-3 py-2.5 text-center">
                  <Badge tone={r.type === "debt" ? "red" : "emerald"}>
                    {r.type === "debt" ? "+ hutang" : "− bayar"}
                  </Badge>
                </td>
                <td className={`px-3 py-2.5 text-right font-mono font-bold ${r.type === "debt" ? "text-red-700" : "text-emerald-700"}`}>{fmtIDR(r.amount)}</td>
                <td className="px-3 py-2.5 text-xs text-stone-600 max-w-[200px] truncate" title={r.note}>{r.note || "—"}</td>
                <td className="px-3 py-2.5 text-center">
                  {r.has_attachment
                    ? <a href={`${BACKEND}/api/employee-debts/${r.id}/attachment`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs" data-testid={`hutang-att-${r.id}`}>
                        <Paperclip className="h-3.5 w-3.5" /> lihat
                      </a>
                    : <span className="text-stone-300">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-700 px-2" title="Edit" data-testid={`hutang-edit-${r.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(r)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`hutang-del-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        {data.total_pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-stone-100 text-xs text-stone-600">
            <span>Halaman {data.page} dari {data.total_pages} · Total {data.total} catatan</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-2 py-1 border border-stone-300 hover:bg-stone-50 disabled:opacity-40" data-testid="hutang-page-prev">
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => setPage(Math.min(data.total_pages, page + 1))} disabled={page >= data.total_pages} className="px-2 py-1 border border-stone-300 hover:bg-stone-50 disabled:opacity-40" data-testid="hutang-page-next">
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Catatan Hutang" : (form.type === "debt" ? "Tambah Hutang" : "Catat Pembayaran")} size="md">
        <div className="space-y-3" data-testid="hutang-modal">
          <Field label="Karyawan *">
            <Select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} disabled={!!editing} data-testid="hutang-form-emp">
              <option value="">— Pilih karyawan —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name} {e.employee_code && `(${e.employee_code})`}</option>)}
            </Select>
          </Field>
          <Field label="Tipe *">
            <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="hutang-form-type">
              <option value="debt">Hutang (+)</option>
              <option value="payment">Pembayaran (−)</option>
            </Select>
          </Field>
          <Field label="Jumlah (Rp) *">
            <Input type="number" min="1" step="any" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="hutang-form-amount" />
          </Field>
          {/* Iter v34cu (#5) — Tanggal transaksi hutang / pembayaran (di luar payroll). */}
          <Field label="Tanggal Transaksi *" hint="Tanggal karyawan menerima pinjaman / melakukan pembayaran">
            <DatePicker value={form.entry_date || ""} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="hutang-form-date" />
          </Field>
          <Field label="Catatan">
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Misal: pinjaman pribadi, advance gaji, pembayaran cicilan, dsb." data-testid="hutang-form-note" />
          </Field>
          <Field label={editing ? "Ganti bukti (opsional)" : "Lampiran bukti (opsional, max 3MB)"} hint="Bisa: gambar (.jpg/.png) atau PDF">
            <input type="file" accept="image/*,application/pdf" onChange={onFileChange} data-testid="hutang-form-file"
              className="w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:border file:border-stone-300 file:bg-stone-50 file:text-stone-700 hover:file:bg-stone-100" />
            {form.attachment_filename && <p className="text-xs text-emerald-700 mt-1"><Paperclip className="h-3 w-3 inline" /> {form.attachment_filename}</p>}
          </Field>
          <div className="flex justify-end gap-2 pt-4 border-t border-stone-100">
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="hutang-save">{editing ? "Simpan Perubahan" : "Simpan"}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
